// eslint-disable-next-line @typescript-eslint/triple-slash-reference -- dependent workspaces type-check runtime-v2 source directly.
/// <reference path="./fast-formula-parser.d.ts" />

import FormulaParserModule from "fast-formula-parser"
import type { ActionConsumerContract, RuntimeActionHandler } from "./action-consumer-contract"

const FormulaParser = FormulaParserModule as unknown as FormulaParserConstructor

interface FormulaParserConstructor {
  new(config?: {
    functions?: Record<string, (...args: unknown[]) => unknown>
    functionsNeedContext?: Record<string, (...args: unknown[]) => unknown>
    onVariable?: (name: string, sheetName?: string) => unknown
    onCell?: (ref: FormulaCellRef) => unknown
    onRange?: (ref: FormulaRangeRef) => unknown[][]
  }): {
    parse(inputText: string, position?: FormulaPosition, allowReturnArray?: boolean): unknown
  }
  DepParser?: new(config?: {
    onVariable?: (name: string, sheetName?: string) => unknown
  }) => {
    parse(inputText: string, position?: FormulaPosition, ignoreError?: boolean): unknown[]
  }
}

export interface FormulaPosition {
  sheet?: string
  row?: number
  col?: number
}

export interface FormulaCellRef {
  sheet?: string
  row: number
  col: number
}

export interface FormulaRangeRef {
  sheet?: string
  from: { row: number; col: number }
  to: { row: number; col: number }
}

export interface FormulaEvalParams {
  formula?: string
  expression?: string
  position?: FormulaPosition
  allowReturnArray?: boolean
  cells?: Record<string, unknown>
  sheets?: Record<string, unknown[][]>
  namedRefs?: Record<string, unknown>
  functions?: Record<string, (...args: unknown[]) => unknown>
}

export interface FormulaEvaluationResult {
  engine: "fast-formula-parser"
  engineVersion: "1.0.19"
  formula: string
  normalizedFormula: string
  position: FormulaPosition
  value: unknown
  valueType: string
}

export interface FormulaDependencyResult {
  engine: "fast-formula-parser"
  engineVersion: "1.0.19"
  formula: string
  normalizedFormula: string
  position: FormulaPosition
  dependencies: unknown[]
}

export interface FormulaActionEngine {
  registerBuiltin(contract: ActionConsumerContract, handler: RuntimeActionHandler): void
}

const FORMULA_NAMED_REF_SHEET = "__cogo_named_refs"
const FORMULA_REF_SENTINEL = "__COGO_REF__:"
const FormulaError = (FormulaParser as unknown as { FormulaError?: Record<string, unknown> }).FormulaError
const FORMULA_BOUNDS_CACHE = new WeakMap<object, Map<string, Map<string, { maxRow: number; maxCol: number }>>>()
const FORMULA_ERROR_CODE_RE = /^#(?:NULL!|DIV\/0!|VALUE!|REF!|NAME\?|NUM!|N\/A)$/
const DIRECT_CELL_REFERENCE_FORMULA_RE = /^(?:(?:'[^']+'|[^!(),+\-*/^&=<>\s]+)!)?\$?[A-Z]{1,3}\$?\d+$/iu

function flattenFormulaArgs(values: unknown[]): unknown[] {
  const out: unknown[] = []
  for (const value of values) {
    const unwrapped = unwrapFormulaArg(value)
    if (Array.isArray(unwrapped)) out.push(...flattenFormulaArgs(unwrapped))
    else out.push(unwrapped)
  }
  return out
}

function unwrapFormulaArg(value: unknown): unknown {
  if (value && typeof value === "object" && !Array.isArray(value) && "value" in value) {
    return (value as { value: unknown }).value
  }
  return value
}

function isFormulaParserContext(value: unknown): boolean {
  if (!value || typeof value !== "object" || Array.isArray(value)) return false
  const record = value as Record<string, unknown>
  return Boolean(record.position && record.utils && record.functions)
}

function customFunctionArgs(values: unknown[]): unknown[] {
  return isFormulaParserContext(values[0]) ? values.slice(1) : values
}

function numericFormulaArgs(values: unknown[]): number[] {
  return flattenFormulaArgs(customFunctionArgs(values))
    .filter((value) => value !== null && value !== undefined && value !== "")
    .map((value) => Number(value))
    .filter((value) => Number.isFinite(value))
}

function matchValue(lookup: unknown, values: unknown[], matchType: unknown): number | Record<string, unknown> {
  const normalizedMatchType = Number(unwrapFormulaArg(matchType) ?? 1)
  const target = unwrapFormulaArg(lookup)
  const notAvailable = (FormulaError?.NA ?? { _error: "#N/A" }) as Record<string, unknown>
  if (normalizedMatchType !== 0) return notAvailable
  const index = values.findIndex((value) => unwrapFormulaArg(value) === target)
  return index >= 0 ? index + 1 : notAvailable
}

function isFormulaErrorValue(value: unknown): boolean {
  const unwrapped = unwrapFormulaArg(value)
  return Boolean(
    unwrapped instanceof Error && String(unwrapped.name).startsWith("#") ||
      typeof unwrapped === "string" && FORMULA_ERROR_CODE_RE.test(unwrapped) ||
      unwrapped && typeof unwrapped === "object" && !Array.isArray(unwrapped) && "_error" in unwrapped,
  )
}

function formulaErrorFromCode(value: unknown): unknown {
  if (typeof value !== "string" || !FORMULA_ERROR_CODE_RE.test(value)) return value
  const errorMap = (FormulaError as unknown as { errorMap?: Map<string, unknown> } | undefined)?.errorMap
  return errorMap?.get(value) ?? { _error: value }
}

function normalizeResolvedFormulaValue(value: unknown): unknown {
  if (value === undefined) return null
  return formulaErrorFromCode(value)
}

function formulaIndexNumber(value: unknown): number | null {
  const unwrapped = unwrapFormulaArg(value)
  if (typeof unwrapped === "number" && Number.isFinite(unwrapped)) return Math.trunc(unwrapped)
  if (typeof unwrapped === "string" && unwrapped.trim()) {
    const numeric = Number(unwrapped)
    return Number.isFinite(numeric) ? Math.trunc(numeric) : null
  }
  return null
}

function formulaErrorOrNull(value: unknown): unknown | null {
  const unwrapped = unwrapFormulaArg(value)
  return isFormulaErrorValue(unwrapped) ? unwrapped : null
}

function parserReference(value: unknown): FormulaCellRef | FormulaRangeRef | null {
  if (!value || typeof value !== "object" || Array.isArray(value)) return null
  const ref = (value as { ref?: unknown }).ref
  if (!ref || typeof ref !== "object" || Array.isArray(ref)) return null
  if (isFormulaCellRef(ref) || isFormulaRangeRef(ref)) return ref
  return null
}

function indexArrayValue(array: unknown, rowNum: unknown, columnNum?: unknown): unknown {
  const value = unwrapFormulaArg(array)
  const rowError = formulaErrorOrNull(rowNum)
  if (rowError) return rowError
  const colError = formulaErrorOrNull(columnNum)
  if (colError) return colError
  const rowNumber = formulaIndexNumber(rowNum)
  const columnNumber = columnNum == null ? 1 : formulaIndexNumber(columnNum)
  if (rowNumber == null || columnNumber == null) return FormulaError?.VALUE ?? { _error: "#VALUE!" }
  const row = Math.max(1, rowNumber) - 1
  const col = Math.max(1, columnNumber) - 1
  if (!Array.isArray(value)) return FormulaError?.VALUE ?? { _error: "#VALUE!" }
  const selectedRow = Array.isArray(value[0]) ? value[row] as unknown[] | undefined : value
  if (!selectedRow) return FormulaError?.REF ?? { _error: "#REF!" }
  return Array.isArray(selectedRow) ? selectedRow[col] ?? (FormulaError?.REF ?? { _error: "#REF!" }) : selectedRow
}

function indexReferenceValue(
  ref: FormulaCellRef | FormulaRangeRef,
  rowNum: unknown,
  columnNum: unknown,
  params: FormulaEvalParams,
  defaultSheet?: string,
): unknown {
  const rowError = formulaErrorOrNull(rowNum)
  if (rowError) return rowError
  const colError = formulaErrorOrNull(columnNum)
  if (colError) return colError
  const rowNumber = formulaIndexNumber(rowNum)
  const columnNumber = formulaIndexNumber(columnNum)
  if (rowNumber == null || columnNumber == null) return FormulaError?.VALUE ?? { _error: "#VALUE!" }
  const row = Math.max(1, rowNumber)
  const col = Math.max(1, columnNumber)
  if (isFormulaCellRef(ref)) {
    return resolveFormulaCell(ref, params, defaultSheet)
  }
  const fromRow = Math.min(ref.from.row, ref.to.row)
  const fromCol = Math.min(ref.from.col, ref.to.col)
  return resolveFormulaCell(
    {
      sheet: ref.sheet,
      row: fromRow + row - 1,
      col: fromCol + col - 1,
    },
    params,
    defaultSheet,
  )
}

function offsetReferenceValue(
  ref: FormulaCellRef | FormulaRangeRef,
  rows: unknown,
  cols: unknown,
  height: unknown,
  width: unknown,
  params: FormulaEvalParams,
  defaultSheet?: string,
): unknown {
  const rowOffset = formulaIndexNumber(rows)
  const colOffset = formulaIndexNumber(cols)
  const rowCount = height == null ? 1 : formulaIndexNumber(height)
  const colCount = width == null ? 1 : formulaIndexNumber(width)
  if (rowOffset == null || colOffset == null || rowCount == null || colCount == null) {
    return FormulaError?.VALUE ?? { _error: "#VALUE!" }
  }
  const baseRow = isFormulaCellRef(ref) ? ref.row : Math.min(ref.from.row, ref.to.row)
  const baseCol = isFormulaCellRef(ref) ? ref.col : Math.min(ref.from.col, ref.to.col)
  const fromRow = baseRow + rowOffset
  const fromCol = baseCol + colOffset
  if (fromRow < 1 || fromCol < 1 || rowCount < 1 || colCount < 1) return FormulaError?.REF ?? { _error: "#REF!" }
  if (rowCount === 1 && colCount === 1) {
    return resolveFormulaCell({ sheet: ref.sheet, row: fromRow, col: fromCol }, params, defaultSheet)
  }
  return resolveFormulaRange(
    {
      sheet: ref.sheet,
      from: { row: fromRow, col: fromCol },
      to: { row: fromRow + rowCount - 1, col: fromCol + colCount - 1 },
    },
    params,
    defaultSheet,
  )
}

function referenceFromSentinel(value: unknown, defaultSheet?: string): FormulaCellRef | null {
  const raw = unwrapFormulaArg(value)
  if (typeof raw !== "string" || !raw.startsWith(FORMULA_REF_SENTINEL)) return null
  const token = raw.slice(FORMULA_REF_SENTINEL.length)
  const bangIndex = token.lastIndexOf("!")
  const sheet = bangIndex >= 0 ? sheetNameFromReferencePrefix(`${token.slice(0, bangIndex)}!`, defaultSheet) : defaultSheet
  const address = bangIndex >= 0 ? token.slice(bangIndex + 1) : token
  const pos = parseCellAddress(address)
  return pos ? { sheet, row: pos.row, col: pos.col } : null
}

function rawFormulaCellValue(ref: FormulaCellRef, params: FormulaEvalParams, defaultSheet?: string): unknown {
  const sheet = ref.sheet ?? defaultSheet
  const directKey = formulaRefKey(ref, defaultSheet)
  const bareKey = `${columnName(ref.col)}${ref.row}`
  const cells = params.cells ?? {}
  if (Object.prototype.hasOwnProperty.call(cells, directKey)) return cells[directKey]
  if (Object.prototype.hasOwnProperty.call(cells, bareKey)) return cells[bareKey]
  if (sheet && params.sheets?.[sheet]?.[ref.row - 1]) return params.sheets[sheet][ref.row - 1][ref.col - 1]
  if (!sheet && params.sheets) {
    const firstSheet = Object.values(params.sheets)[0]
    return firstSheet?.[ref.row - 1]?.[ref.col - 1]
  }
  return undefined
}

function isBlankFormulaRef(ref: FormulaCellRef, params: FormulaEvalParams, defaultSheet?: string): boolean {
  const value = rawFormulaCellValue(ref, params, defaultSheet)
  return value === null || value === undefined || value === ""
}

function makeContextFormulaFunctions(params: FormulaEvalParams, defaultSheet?: string): Record<string, (...args: unknown[]) => unknown> {
  return {
    INDEX: (_context: unknown, array: unknown, rowNum: unknown, columnNum: unknown = 1) => {
      const ref = parserReference(array)
      return ref ? indexReferenceValue(ref, rowNum, columnNum, params, defaultSheet) : indexArrayValue(array, rowNum, columnNum)
    },
    OFFSET: (_context: unknown, reference: unknown, rows: unknown, cols: unknown, height: unknown = 1, width: unknown = 1) => {
      const ref = parserReference(reference) ?? referenceFromSentinel(reference, defaultSheet)
      return ref ? offsetReferenceValue(ref, rows, cols, height, width, params, defaultSheet) : FormulaError?.VALUE ?? { _error: "#VALUE!" }
    },
    COGO_ISBLANK: (_context: unknown, reference: unknown) => {
      const ref = referenceFromSentinel(reference, defaultSheet)
      return ref ? isBlankFormulaRef(ref, params, defaultSheet) : false
    },
  }
}

function parseCountIfCriteria(criteria: unknown) {
  const raw = unwrapFormulaArg(criteria)
  const text = String(raw ?? "")
  const match = text.match(/^(<>|>=|<=|>|<|=)?(.*)$/)
  const operator = match?.[1] ?? "="
  const operandText = match?.[2] ?? text
  const numericOperand = Number(operandText)
  return {
    operator,
    operand: Number.isFinite(numericOperand) && operandText.trim() !== "" ? numericOperand : operandText,
  }
}

function countIfMatches(value: unknown, criteria: unknown): boolean {
  const unwrapped = unwrapFormulaArg(value)
  const { operator, operand } = parseCountIfCriteria(criteria)
  if (typeof operand === "number") {
    if (typeof unwrapped !== "number" || !Number.isFinite(unwrapped)) return false
    if (operator === ">") return unwrapped > operand
    if (operator === ">=") return unwrapped >= operand
    if (operator === "<") return unwrapped < operand
    if (operator === "<=") return unwrapped <= operand
    if (operator === "<>") return unwrapped !== operand
    return unwrapped === operand
  }
  const text = String(unwrapped ?? "")
  const expected = String(operand)
  if (operator === "<>") return text !== expected
  return text === expected
}

const DEFAULT_FORMULA_FUNCTIONS: Record<string, (...args: unknown[]) => unknown> = {
  MAX: (...args: unknown[]) => {
    const values = numericFormulaArgs(args)
    return values.length ? Math.max(...values) : 0
  },
  MIN: (...args: unknown[]) => {
    const values = numericFormulaArgs(args)
    return values.length ? Math.min(...values) : 0
  },
  CHOOSE: (...args: unknown[]) => {
    const [index, ...choices] = customFunctionArgs(args)
    const selected = Number(unwrapFormulaArg(index))
    if (!Number.isFinite(selected) || selected < 1) return null
    return unwrapFormulaArg(choices[Math.trunc(selected) - 1]) ?? null
  },
  MATCH: (...args: unknown[]) => {
    const [lookup, lookupArray, matchType = 1] = customFunctionArgs(args)
    const values = flattenFormulaArgs([lookupArray])
    return matchValue(lookup, values, matchType)
  },
  COUNTIF: (...args: unknown[]) => {
    const [range, criteria] = customFunctionArgs(args)
    return flattenFormulaArgs([range]).filter((value) => countIfMatches(value, criteria)).length
  },
  COUNT: (...args: unknown[]) => {
    return flattenFormulaArgs(customFunctionArgs(args))
      .filter((value) => {
        const unwrapped = unwrapFormulaArg(value)
        return typeof unwrapped === "number" && Number.isFinite(unwrapped)
      })
      .length
  },
  COUNTA: (...args: unknown[]) => {
    return flattenFormulaArgs(customFunctionArgs(args))
      .filter((value) => {
        const unwrapped = unwrapFormulaArg(value)
        return unwrapped !== null && unwrapped !== undefined && unwrapped !== ""
      })
      .length
  },
  ISERROR: (...args: unknown[]) => {
    const [value] = customFunctionArgs(args)
    return isFormulaErrorValue(value)
  },
  PRODUCT: (...args: unknown[]) => {
    const flattened = flattenFormulaArgs(customFunctionArgs(args))
    const error = flattened.find((value) => isFormulaErrorValue(value))
    if (error) return unwrapFormulaArg(error)
    const values = numericFormulaArgs(flattened)
    return values.reduce((product, value) => product * value, 1)
  },
  RANK: (...args: unknown[]) => {
    const [number, ref, order = 0] = customFunctionArgs(args)
    const target = Number(unwrapFormulaArg(number))
    if (!Number.isFinite(target)) return FormulaError?.NA ?? { _error: "#N/A" }
    const values = flattenFormulaArgs([ref])
      .map((value) => Number(unwrapFormulaArg(value)))
      .filter((value) => Number.isFinite(value))
    const ascending = Number(unwrapFormulaArg(order)) !== 0
    return 1 + values.filter((value) => ascending ? value < target : value > target).length
  },
}

function columnName(col: number): string {
  let value = col
  let out = ""
  while (value > 0) {
    const mod = (value - 1) % 26
    out = String.fromCharCode(65 + mod) + out
    value = Math.floor((value - mod) / 26)
  }
  return out
}

function columnNameToNumber(name: string): number {
  return name.toUpperCase().split("").reduce((value, char) => value * 26 + char.charCodeAt(0) - 64, 0)
}

function parseCellAddress(address: string): { col: number; row: number } | null {
  const match = address.replace(/\$/g, "").match(/^([A-Z]{1,3})(\d+)$/i)
  if (!match) return null
  return { col: columnNameToNumber(match[1]), row: Number.parseInt(match[2], 10) }
}

function updateSheetBounds(bounds: Map<string, { maxRow: number; maxCol: number }>, sheet: string, row: number, col: number) {
  const current = bounds.get(sheet) ?? { maxRow: 1, maxCol: 1 }
  current.maxRow = Math.max(current.maxRow, row)
  current.maxCol = Math.max(current.maxCol, col)
  bounds.set(sheet, current)
}

function inferFormulaSheetBounds(params: FormulaEvalParams, defaultSheet?: string) {
  const cacheOwner =
    params.cells && typeof params.cells === "object"
      ? params.cells
      : params.sheets && typeof params.sheets === "object"
        ? params.sheets
        : undefined
  const cacheKey = defaultSheet ?? ""
  if (cacheOwner) {
    const ownerCache = FORMULA_BOUNDS_CACHE.get(cacheOwner)
    const cached = ownerCache?.get(cacheKey)
    if (cached) return cached
  }
  const bounds = new Map<string, { maxRow: number; maxCol: number }>()
  for (const [sheet, rows] of Object.entries(params.sheets ?? {})) {
    updateSheetBounds(bounds, sheet, rows.length, Math.max(1, ...rows.map((row) => row.length)))
  }
  for (const key of Object.keys(params.cells ?? {})) {
    const bangIndex = key.lastIndexOf("!")
    const sheet = bangIndex >= 0 ? key.slice(0, bangIndex) : defaultSheet
    const address = bangIndex >= 0 ? key.slice(bangIndex + 1) : key
    const pos = parseCellAddress(address)
    if (sheet && pos) updateSheetBounds(bounds, sheet, pos.row, pos.col)
  }
  if (cacheOwner) {
    const ownerCache = FORMULA_BOUNDS_CACHE.get(cacheOwner) ?? new Map<string, Map<string, { maxRow: number; maxCol: number }>>()
    ownerCache.set(cacheKey, bounds)
    FORMULA_BOUNDS_CACHE.set(cacheOwner, ownerCache)
  }
  return bounds
}

function sheetNameFromReferencePrefix(prefix: string | undefined, defaultSheet?: string): string | undefined {
  if (!prefix) return defaultSheet
  const raw = prefix.slice(0, -1)
  return raw.startsWith("'") && raw.endsWith("'") ? raw.slice(1, -1).replace(/''/g, "'") : raw
}

function boundsForSheet(
  bounds: Map<string, { maxRow: number; maxCol: number }>,
  sheet: string | undefined,
) {
  return sheet ? bounds.get(sheet) : undefined
}

function normalizeWholeRowColumnRanges(inputText: string, params: FormulaEvalParams, defaultSheet?: string): string {
  const bounds = inferFormulaSheetBounds(params, defaultSheet)
  const sheetPrefix = "(?:'[^']+'|[^!(),+\\-*/^&=<>\\s]+)!"
  const rowRangePattern = new RegExp(`(${sheetPrefix})?\\$?([1-9][0-9]*):\\$?([1-9][0-9]*)`, "gu")
  const colRangePattern = new RegExp(`(${sheetPrefix})?\\$?([A-Z]{1,3}):\\$?([A-Z]{1,3})`, "gu")
  return inputText
    .replace(rowRangePattern, (match, prefix: string | undefined, startRow: string, endRow: string) => {
      const sheet = sheetNameFromReferencePrefix(prefix, defaultSheet)
      const sheetBounds = boundsForSheet(bounds, sheet)
      if (!sheetBounds) return match
      return `${prefix ?? ""}A${startRow}:${columnName(sheetBounds.maxCol)}${endRow}`
    })
    .replace(colRangePattern, (match, prefix: string | undefined, startCol: string, endCol: string) => {
      const sheet = sheetNameFromReferencePrefix(prefix, defaultSheet)
      const sheetBounds = boundsForSheet(bounds, sheet)
      if (!sheetBounds) return match
      return `${prefix ?? ""}${startCol}1:${endCol}${sheetBounds.maxRow}`
    })
}

function quoteOffsetReferenceArgs(inputText: string): string {
  const referencePattern = "((?:'[^']+'|[^!(),+\\-*/^&=<>\\s]+)!)?(\\$?[A-Z]{1,3}\\$?\\d+)"
  const offsetPattern = new RegExp(`\\bOFFSET\\(\\s*${referencePattern}\\s*,`, "giu")
  return inputText.replace(offsetPattern, (_match, sheetPrefix: string | undefined, address: string) => {
    return `OFFSET("${FORMULA_REF_SENTINEL}${sheetPrefix ?? ""}${address}",`
  })
}

function quoteBlankEqualityChecks(inputText: string): string {
  const referencePattern = "((?:'[^']+'|[^!(),+\\-*/^&=<>\\s]+)!)?(\\$?[A-Z]{1,3}\\$?\\d+)"
  const refEqualsBlank = new RegExp(`${referencePattern}\\s*=\\s*""`, "giu")
  const blankEqualsRef = new RegExp(`""\\s*=\\s*${referencePattern}`, "giu")
  const replaceWithIsBlank = (_match: string, sheetPrefix: string | undefined, address: string) =>
    `COGO_ISBLANK("${FORMULA_REF_SENTINEL}${sheetPrefix ?? ""}${address}")`
  return inputText
    .replace(refEqualsBlank, replaceWithIsBlank)
    .replace(blankEqualsRef, replaceWithIsBlank)
}

function normalizeFormulaInput(formula: string, params: FormulaEvalParams, defaultSheet?: string): string {
  const normalizedRanges = normalizeWholeRowColumnRanges(formula.trim().replace(/^=/, ""), params, defaultSheet)
  return quoteOffsetReferenceArgs(quoteBlankEqualityChecks(normalizedRanges))
}

function isDirectCellReferenceFormula(inputText: string): boolean {
  return DIRECT_CELL_REFERENCE_FORMULA_RE.test(inputText.trim())
}

function directCellReferenceFromFormula(inputText: string, defaultSheet?: string): FormulaCellRef | null {
  const trimmed = inputText.trim()
  if (!isDirectCellReferenceFormula(trimmed)) return null
  const bangIndex = trimmed.lastIndexOf("!")
  const prefix = bangIndex >= 0 ? trimmed.slice(0, bangIndex + 1) : undefined
  const address = bangIndex >= 0 ? trimmed.slice(bangIndex + 1) : trimmed
  const pos = parseCellAddress(address)
  return pos ? { sheet: sheetNameFromReferencePrefix(prefix, defaultSheet), row: pos.row, col: pos.col } : null
}

function formulaRefKey(ref: FormulaCellRef, defaultSheet?: string): string {
  const cell = `${columnName(ref.col)}${ref.row}`
  const sheet = ref.sheet ?? defaultSheet
  return sheet ? `${sheet}!${cell}` : cell
}

function resolveFormulaCell(ref: FormulaCellRef, params: FormulaEvalParams, defaultSheet?: string): unknown {
  const sheet = ref.sheet ?? defaultSheet
  const directKey = formulaRefKey(ref, defaultSheet)
  const bareKey = `${columnName(ref.col)}${ref.row}`
  const cells = params.cells ?? {}
  if (Object.prototype.hasOwnProperty.call(cells, directKey)) return normalizeResolvedFormulaValue(cells[directKey])
  if (Object.prototype.hasOwnProperty.call(cells, bareKey)) return normalizeResolvedFormulaValue(cells[bareKey])
  if (sheet && params.sheets?.[sheet]?.[ref.row - 1]) {
    return normalizeResolvedFormulaValue(params.sheets[sheet][ref.row - 1][ref.col - 1])
  }
  if (!sheet && params.sheets) {
    const firstSheet = Object.values(params.sheets)[0]
    return normalizeResolvedFormulaValue(firstSheet?.[ref.row - 1]?.[ref.col - 1])
  }
  return null
}

function resolveFormulaRange(ref: FormulaRangeRef, params: FormulaEvalParams, defaultSheet?: string): unknown[][] {
  const values: unknown[][] = []
  const fromRow = Math.min(ref.from.row, ref.to.row)
  const toRow = Math.max(ref.from.row, ref.to.row)
  const fromCol = Math.min(ref.from.col, ref.to.col)
  const toCol = Math.max(ref.from.col, ref.to.col)
  for (let row = fromRow; row <= toRow; row += 1) {
    const inner: unknown[] = []
    for (let col = fromCol; col <= toCol; col += 1) {
      inner.push(resolveFormulaCell({ sheet: ref.sheet, row, col }, params, defaultSheet))
    }
    values.push(inner)
  }
  return values
}

function isFormulaCellRef(value: unknown): value is FormulaCellRef {
  if (!value || typeof value !== "object" || Array.isArray(value)) return false
  const record = value as Record<string, unknown>
  return typeof record.row === "number" && typeof record.col === "number"
}

function isFormulaRangeRef(value: unknown): value is FormulaRangeRef {
  if (!value || typeof value !== "object" || Array.isArray(value)) return false
  const record = value as Record<string, unknown>
  const from = record.from as Record<string, unknown> | undefined
  const to = record.to as Record<string, unknown> | undefined
  return Boolean(
    from &&
      to &&
      typeof from.row === "number" &&
      typeof from.col === "number" &&
      typeof to.row === "number" &&
      typeof to.col === "number",
  )
}

function formulaValueType(value: unknown): string {
  if (Array.isArray(value)) return "array"
  if (value === null) return "null"
  return typeof value
}

export function evaluateFormula(params: FormulaEvalParams): FormulaEvaluationResult {
  const formula = String(params.formula ?? params.expression ?? "")
  if (!formula.trim()) throw new Error("Formula action requires params.formula or params.expression")
  const position = params.position ?? { sheet: "Sheet1", row: 1, col: 1 }
  const normalizedFormula = normalizeFormulaInput(formula, params, position.sheet)
  const directRef = directCellReferenceFromFormula(normalizedFormula, position.sheet)
  if (directRef) {
    const directValue = resolveFormulaCell(directRef, params, position.sheet)
    const value = directValue === null ? 0 : directValue
    return {
      engine: "fast-formula-parser",
      engineVersion: "1.0.19",
      formula,
      normalizedFormula,
      position,
      value,
      valueType: formulaValueType(value),
    }
  }
  const namedRefs = params.namedRefs ?? {}
  const namedScalarKeys = Object.keys(namedRefs).filter((key) => {
    const value = namedRefs[key]
    return !isFormulaCellRef(value) && !isFormulaRangeRef(value)
  })
  const parser = new FormulaParser({
    functions: { ...DEFAULT_FORMULA_FUNCTIONS, ...(params.functions ?? {}) },
    functionsNeedContext: makeContextFormulaFunctions(params, position.sheet),
    onVariable: (name, sheetName) => {
      const directKey = Object.prototype.hasOwnProperty.call(namedRefs, name) ? name : undefined
      const sheetKey = sheetName && Object.prototype.hasOwnProperty.call(namedRefs, `${sheetName}!${name}`)
        ? `${sheetName}!${name}`
        : undefined
      const key = sheetKey ?? directKey
      if (!key) return null
      const value = namedRefs[key]
      if (isFormulaCellRef(value) || isFormulaRangeRef(value)) return value
      return { sheet: FORMULA_NAMED_REF_SHEET, row: namedScalarKeys.indexOf(key) + 1, col: 1 }
    },
    onCell: (ref) => {
      if (ref.sheet === FORMULA_NAMED_REF_SHEET) {
        const key = namedScalarKeys[ref.row - 1]
        return key ? namedRefs[key] : 0
      }
      return resolveFormulaCell(ref, params, position.sheet)
    },
    onRange: (ref) => {
      if (ref.sheet === FORMULA_NAMED_REF_SHEET) {
        return resolveFormulaRange(ref, { ...params, sheets: { [FORMULA_NAMED_REF_SHEET]: namedScalarKeys.map((key) => [namedRefs[key]]) } }, FORMULA_NAMED_REF_SHEET)
      }
      return resolveFormulaRange(ref, params, position.sheet)
    },
  })
  const parsedValue = parser.parse(normalizedFormula, position, params.allowReturnArray === true)
  const value = parsedValue === null && isDirectCellReferenceFormula(normalizedFormula) ? 0 : parsedValue
  return {
    engine: "fast-formula-parser",
    engineVersion: "1.0.19",
    formula,
    normalizedFormula,
    position,
    value,
    valueType: formulaValueType(value),
  }
}

export function extractFormulaDependencies(params: FormulaEvalParams): FormulaDependencyResult {
  if (!FormulaParser.DepParser) throw new Error("Formula dependency parser is not available")
  const formula = String(params.formula ?? params.expression ?? "")
  if (!formula.trim()) throw new Error("Formula dependency action requires params.formula or params.expression")
  const position = params.position ?? { sheet: "Sheet1", row: 1, col: 1 }
  const normalizedFormula = normalizeFormulaInput(formula, params, position.sheet)
  const namedRefs = params.namedRefs ?? {}
  const namedScalarKeys = Object.keys(namedRefs).filter((key) => {
    const value = namedRefs[key]
    return !isFormulaCellRef(value) && !isFormulaRangeRef(value)
  })
  const parser = new FormulaParser.DepParser({
    onVariable: (name, sheetName) => {
      const directKey = Object.prototype.hasOwnProperty.call(namedRefs, name) ? name : undefined
      const sheetKey = sheetName && Object.prototype.hasOwnProperty.call(namedRefs, `${sheetName}!${name}`)
        ? `${sheetName}!${name}`
        : undefined
      const key = sheetKey ?? directKey
      if (!key) return null
      const value = namedRefs[key]
      if (isFormulaCellRef(value) || isFormulaRangeRef(value)) return value
      return { sheet: FORMULA_NAMED_REF_SHEET, row: namedScalarKeys.indexOf(key) + 1, col: 1 }
    },
  })
  return {
    engine: "fast-formula-parser",
    engineVersion: "1.0.19",
    formula,
    normalizedFormula,
    position,
    dependencies: parser.parse(normalizedFormula, position, true),
  }
}

export function registerFormulaActions(engine: FormulaActionEngine): void {
  engine.registerBuiltin(formulaContract("cogo.formula.evaluate"), (params) => evaluateFormula(params as FormulaEvalParams))
  engine.registerBuiltin(formulaContract("cogo.formula.dependencies"), (params) => extractFormulaDependencies(params as FormulaEvalParams))
}

function formulaContract(action: string): ActionConsumerContract {
  return {
    schemaVersion: "cogo.action-consumer.v1",
    action,
    runtimeAliases: [],
    reads: [
      { path: "formula", aliases: ["expression"], required: true, role: "input" },
      { path: "position", required: false, role: "input" },
      { path: "allowReturnArray", required: false, role: "guard" },
      { path: "cells", required: false, role: "payload" },
      { path: "sheets", required: false, role: "payload" },
      { path: "namedRefs", required: false, role: "payload" },
      { path: "functions", required: false, role: "payload" },
    ],
    outcome: { kind: "return-capture", capture: "step-name" },
    requiredHostCapabilities: [],
    missingConsumerPolicy: "reject-before-side-effects",
  }
}
